import "./style.scss";
import { Button, Modal, Spin } from "antd";
import moment from "moment-timezone";
import { useEffect, useMemo, useState } from "react";

import { SHIFT_CANCELLATION_REASON_TYPES } from "../../constants/shifts";
import { getWorkerShiftTypeLabel } from "../../appV2/redesign/utils/worker";
import { ShiftDescription } from "../shiftDescription";

import { getWorkerDepartureStatus } from "@src/api/shifts";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { HCF_USER_EVENTS } from "@src/constants/analyticsEvents";
import { getWorkerLeftEarlyMetadata } from "@src/containers/facilityDashboard/ShiftCalendar/dayView/facilityShiftCard/workerLeftEarlyMetadata";
import { Session, SessionType } from "@src/modules/interface";
import { useSelector } from "react-redux";
import { UrgencyType } from "../../containers/facilityDashboard/ShiftCalendar/dayView/facilityShiftCard/constants";
import { ShiftCancellationModalTitle } from "../shiftCancellationModalTitle";
import { UrgentShiftCancelModel } from "../UrgentShiftCancelModal";
import { modalReasonTypeStyle, modalWorkerNameStyle } from "./style";
import { WorkerNoShowModalProps } from "./workerNoShowModal.type";
import { isDefined } from "@clipboard-health/util-ts";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { NoShowFormContent } from "@src/appV2/redesign/Shift/components/NoShowFormContent";
import { NoShowWarningContent } from "@src/appV2/redesign/Shift/components/NoShowWarningContent";
import { useNoShowWarning } from "@src/appV2/redesign/Shift/hooks/useNoShowWarning";
import { messageString } from "../../utils/messageStrings/strings";

function WorkerNoShowModal({ shift, visible, onConfirm, onCancel }: WorkerNoShowModalProps) {
  const [reasonDescription, setReasonDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [isUrgentShiftToBeCanceled, setIsUrgentShiftToBeCanceled] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [replacementSelection, setReplacementSelection] = useState<boolean | null>(null);
  const [emptyCancellationReasonError, setEmptyCancellationReasonError] = useState(false);
  const { user } = useSelector<SessionType, Session>((state) => state.session);

  const workplaceId = shift?.facility?.userId;
  const shiftId = shift?._id ?? "";
  const workerId = shift?.agentId ?? "";

  const {
    step,
    warningType,
    tripData,
    handleWarningConfirm,
    onWarningBack,
    onWarningClose,
    resetWarning,
  } = useNoShowWarning({
    workplaceId,
    shiftId,
    workerId,
    shiftStart: shift?.start,
    enabled: visible && isDefined(workplaceId) && isDefined(shift?._id),
  });

  useEffect(() => {
    setReasonDescription("");
    setIsConfirmed(false);
    setReplacementSelection(null);
    setLoading(false);
    setEmptyCancellationReasonError(false);
    setIsUrgentShiftToBeCanceled(false);
    resetWarning();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, shift?._id]);

  const shouldShowUrgentCancelShiftModal = useMemo(() => {
    const shiftEnd = shift?.end;
    const facilityTmz = shift?.facility?.tmz;

    const hasShiftEnded =
      shiftEnd &&
      facilityTmz &&
      moment.tz(shiftEnd, facilityTmz).isBefore(moment().tz(facilityTmz));

    return (
      visible && !isUrgentShiftToBeCanceled && shift?.urgency === UrgencyType.NCNS && !hasShiftEnded
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUrgentShiftToBeCanceled, visible]);

  const isAlreadyRequestingReplacement =
    shift?.urgency === UrgencyType.NCNS &&
    (shift?.isRequestingReplacement === true || isUrgentShiftToBeCanceled);

  // @ts-expect-error TODO(typecheck-unify): TS7030 Not all code paths return a value.
  const onConfirmWorkerNoShow = async () => {
    if (!isConfirmed) {
      showErrorToast("Please read and accept the terms in order to proceed with your request");
      return;
    }

    if (!reasonDescription) {
      setEmptyCancellationReasonError(true);
      return showErrorToast("Please add additional details.");
    }

    if (
      shift?.canRequestReplacement &&
      !isAlreadyRequestingReplacement &&
      replacementSelection === null
    ) {
      showErrorToast("Please select if you'd like us to post an urgent replacement shift.");
      return;
    }

    setLoading(true);

    await onConfirm(
      SHIFT_CANCELLATION_REASON_TYPES.NO_CALL_NO_SHOW,
      reasonDescription,
      isAlreadyRequestingReplacement ? false : replacementSelection === true
    );
    await trackWorkerNoShowOrLeftEarlyEvent();

    setLoading(false);
  };

  async function trackWorkerNoShowOrLeftEarlyEvent() {
    if (!isDefined(shift)) {
      return;
    }
    try {
      const result = await getWorkerDepartureStatus(shift._id);
      if (shift?.canRequestReplacement && replacementSelection === true && shift._id) {
        const EVENT_NAME = shift?.isRequestingReplacement
          ? HCF_USER_EVENTS.WORKER_LEFT_EARLY_REPLACEMENT_REQUEST
          : HCF_USER_EVENTS.FACILITY_NO_SHOW_REPLACEMENT_REQUEST;
        const leftEarlyMetadata = getWorkerLeftEarlyMetadata({
          shift,
          user,
          timeLeftAt: result?.shift?.attendance?.leftFacilityGeofenceAt,
          requestReplacementReason: reasonDescription,
        });
        logEvent(EVENT_NAME, leftEarlyMetadata);
      }
    } catch (error) {
      logError("Error sending segment event for tracking worker no show / request replacement", {
        error,
      });
    }
  }

  function handleWarningBack() {
    onWarningBack();
    setIsUrgentShiftToBeCanceled(false);
    onCancel();
  }

  function handleMainCancel() {
    onWarningClose();
    setIsUrgentShiftToBeCanceled(false);
    onCancel();
  }

  if (shouldShowUrgentCancelShiftModal && shift) {
    return (
      <UrgentShiftCancelModel
        shift={shift}
        onKeepShift={() => {
          setIsUrgentShiftToBeCanceled(false);
          onCancel();
        }}
        onCancelShift={() => {
          setIsUrgentShiftToBeCanceled(true);
        }}
      />
    );
  }

  function getModalFooter() {
    if (step === "warning") {
      return warningFooter;
    }
    if (step === "loading") {
      return [];
    }
    return formFooter;
  }

  const warningFooter = [
    <Button key="back" onClick={handleWarningBack}>
      Back
    </Button>,
    <Button key="confirm" type="primary" danger onClick={handleWarningConfirm}>
      Confirm No Show
    </Button>,
  ];

  const formFooter = [
    <Button key="cancel" onClick={handleMainCancel}>
      No, cancel action
    </Button>,
    <Button
      key="ok"
      type="primary"
      danger
      loading={loading}
      disabled={loading}
      aria-label="Mark professional No Show"
      onClick={onConfirmWorkerNoShow}
    >
      No Show
    </Button>,
  ];

  return (
    <Modal
      className="worker-no-show-modal"
      width={537}
      title={
        <ShiftCancellationModalTitle title="Do you want to mark this professional as a No Show?" />
      }
      visible={visible}
      onCancel={handleMainCancel}
      footer={getModalFooter()}
      destroyOnClose={true}
    >
      {step === "loading" && (
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <Spin />
        </div>
      )}

      {step === "warning" &&
        isDefined(warningType) &&
        isDefined(tripData) &&
        isDefined(tripData.currentEtaDistance) &&
        isDefined(tripData.currentEtaDuration) && (
          <NoShowWarningContent
            warningType={warningType}
            distanceInM={tripData.currentEtaDistance}
            etaDurationInMin={tripData.currentEtaDuration}
            lastLocationReceivedByServerAt={tripData.lastLocationReceivedByServerAt}
            trackingUrl={tripData.trackingUrl}
          />
        )}

      {step === "form" && (
        <div>
          {shift?.agent && (
            <h3 style={modalWorkerNameStyle}>
              {shift.agent.name},{" "}
              {getWorkerShiftTypeLabel(shift.agentReq, shift.facility?.fullAddress?.state)}
            </h3>
          )}

          <ShiftDescription shift={shift} isCancel={false} />

          <h2 style={modalReasonTypeStyle}>No Show</h2>

          <NoShowFormContent
            canRequestReplacement={shift?.canRequestReplacement}
            confirmLabel={messageString.noCallNoShowCheckBoxMessage}
            hasReasonError={emptyCancellationReasonError}
            isAlreadyRequestingReplacement={isAlreadyRequestingReplacement}
            isConfirmed={isConfirmed}
            reasonDescription={reasonDescription}
            replacementSelection={replacementSelection}
            onConfirmedChange={setIsConfirmed}
            onReasonDescriptionChange={(value) => {
              setReasonDescription(value);
              setEmptyCancellationReasonError(!value);
            }}
            onReplacementSelectionChange={setReplacementSelection}
          />
        </div>
      )}
    </Modal>
  );
}

export { WorkerNoShowModal };
